import { useState, useEffect } from 'react';
import { RedriveApiFactory } from '../../../lusca-search-api/LuscaSearchApiFactory';
import { Stage } from '../../../lusca-search-api/generated-src';

interface OrderItem {
  orderId: string;
  stage: Stage;
}

export const useRedriveOrdersData = (selectedStage: Stage, executionId: string | null) => {
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [isTruncated, setIsTruncated] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  function parseContentRange(range: string) {
    const match = range.match(/^bytes (\d+)-(\d+)\/(\d+|\*)$/);
    if (!match) return null;
    const [, start, end, total] = match;
    return {
      start: +start,
      end: +end,
      total: total === "*" ? null : +total,
    };
  }

  function responseIsTruncated(response: Response): boolean {
    const range = response.headers.get("content-range");
    if (!range) return false;
    const parsed = parseContentRange(range);
    if (!parsed?.total) return true;
    return parsed.end + 1 < parsed.total;
  }

  // Fetch the download URL and then fetch the CSV file
  useEffect(() => {
    const fetchOrders = async () => {
      if (!executionId) {
        setOrders([]);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const redriveApi = RedriveApiFactory();
        const response = await redriveApi.getRedriveOrders(
          executionId,
          selectedStage
        );

        if (response.data && response.data.downloadUrl) {
          setDownloadUrl(response.data.downloadUrl);
          // Fetch only the first 400KB from the presigned URL
          const csvResponse = await fetch(response.data.downloadUrl, {
            headers: {
              Range: "bytes=0-409599",
            },
          });
          if (!csvResponse.ok) {
            throw new Error("Failed to download orders file");
          }

          const csvText = await csvResponse.text();
          const lines = csvText.split("\n");
          // Check if response was truncated
          const wasTruncated = responseIsTruncated(csvResponse);
          setIsTruncated(wasTruncated);
          // Drop the incomplete last line if response was truncated
          const completeLines = wasTruncated ? lines.slice(0, -1) : lines;

          const orderIds = completeLines
            .map((line) => line.trim())
            .filter((line) => line.length > 0)
            .map((line) => line.split(",")[0].trim()); // Take first column if CSV has multiple columns

          const basicOrders = orderIds.map((orderId: string) => ({
            orderId,
            stage: selectedStage,
          }));
          setOrders(basicOrders);
        } else {
          setOrders([]);
          setIsTruncated(false);
          setDownloadUrl(null);
        }
      } catch (error) {
        setError(`Failed to load orders for this redrive execution: ${error instanceof Error ? error.message : String(error)}`);
        setOrders([]);
        setIsTruncated(false);
        setDownloadUrl(null);
      } finally {
        setLoading(false);
      }
    };

    if (selectedStage && executionId) {
      fetchOrders();
    }
  }, [selectedStage, executionId]);

  return { orders, loading, error, isTruncated, downloadUrl };
};
